import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AdditionalItem, AdditionalLabel } from '../../forms/FormWorkflows/AssignmentDropdownItems';

import { isDynamicType } from '../../forms/FormWorkflows/workflowHelpers';

const getEmailHelp = (title, isMultipleChoice, type = 'form') => {
  const helpDescriptorText = isMultipleChoice ? 'email addresses selected' : 'comma separated email addresses defined';
  return `The ${type} will be sent to the ${helpDescriptorText} in the ${title} field`;
};
const getCustomerProjectHelp = (t, isOffice, type = 'Customer', sendType = 'form') => `The ${sendType} will be sent to the ${t(type).toLowerCase()}'s ${isOffice ? 'office' : 'billing'} email address`;
const getSubjectBodyHelp = (title, isSubject) => `The email will use the contents of the ${title} field as the email ${isSubject ? 'subject' : 'body'}`;
const getHelpText = ({
  isCustomer,
  isProject,
  isOffice,
  isSubject,
  isBody,
  title,
  isMultipleChoice,
  type,
  t,
}) => {
  if (isCustomer || isProject) return getCustomerProjectHelp(t, isOffice, isCustomer ? 'Customer' : 'Project', type);
  if (isSubject || isBody) return getSubjectBodyHelp(title, isSubject);
  return getEmailHelp(title, isMultipleChoice, type);
};
export const getOption = (props) => {
  const {
    fieldId,
    title,
    isSubject,
    isBody,
  } = props;

  const HELP_TEXT = getHelpText(props);
  return {
    id: fieldId,
    label: (
      <AdditionalLabel
        title={title}
        help={HELP_TEXT}
        isEmailSubjectBodyLabel={isSubject || isBody}
      />
    ),
    children: (
      <AdditionalItem
        title={title}
        help={HELP_TEXT}
      />
    ),
    title,
  };
};

export default function useFormEmailOptions(sections, type) {
  const { t } = useTranslation();

  const [
    emailFormFields,
    attachmentFieldOptions,
    dateTimeFields,
  ] = useMemo(() => {
    const options = [];
    const fileOptions = [];
    const dateTimeOptions = [];
    let hasCustomer = false;
    let hasProject = false;
    sections.forEach(({ fields = [], name: sectionName }) => {
      fields.forEach((field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: {
            dataType,
            title,
          } = {},
        } = field;
        // for recipient fields, don't include key and name
        if (selectedType === 'dropdown') {
          switch (dataType) {
            case 'Customers': {
              if (!hasCustomer) options.push({ id: fieldId, title, isCustomer: true });
              hasCustomer = true;
              break;
            }
            case 'Projects': {
              if (!hasProject) options.push({ id: fieldId, title, isProject: true });
              hasProject = true;
              break;
            }
            case 'Custom': {
              options.push({ id: fieldId, title, isMultipleChoice: true });
              break;
            }
            default:
              // do nothing
              break;
          }
        }
        // for other fields (e.g. body and subject), don't include id so it won't be added to recipient
        if (selectedType === 'dropdown' || isDynamicType(field, 'dropdown')) {
          options.push({
            key: `${sectionName} - ${title}`,
            name: `${sectionName} - ${title}`,
          });
        } else if (
          selectedType === 'text'
          || selectedType === 'attribute'
          || selectedType === 'staticText'
          || isDynamicType(field, 'text')
          || isDynamicType(field, 'attribute')
          || isDynamicType(field, 'staticText')
        ) {
          options.push({
            id: fieldId, title, key: `${sectionName} - ${title}`, name: `${sectionName} - ${title}`,
          });
        } else if (
          selectedType === 'attachment'
          || selectedType === 'staticAttachments'
          || isDynamicType(field, 'attachment')
          || isDynamicType(field, 'staticAttachments')
        ) {
          fileOptions.push({ value: fieldId, label: title });
        } else if (selectedType === 'dateTime' || isDynamicType(field, 'dateTime')) {
          dateTimeOptions.push({ key: `${sectionName} - ${title}`, name: `${sectionName} - ${title}`, value: fieldId });
        } else if (selectedType === 'table' && dataType === 'Equipment') {
          const { numAnswers = 1, openLimit = true } = field.configProps || {};
          if (numAnswers === 1 && !openLimit) {
            const equipmentFields = [
              'Equipment Id',
              'Equipment Name',
              'Equipment Hours',
              'Equipment Mileage',
            ];
            equipmentFields.forEach((equipmentField) => options.push({ key: `${sectionName} - ${title} - ${equipmentField}`, name: `${sectionName} - ${title} - ${equipmentField}` }));
          }
        }
      });
    });
    return [
      options,
      fileOptions,
      dateTimeOptions,
    ];
  }, [sections]);

  const [
    additionalOptions,
    subjectOptions,
    bodyOptions,
  ] = useMemo(() => {
    const options = [];
    const optionsForBody = [];
    const optionsForSubject = [];
    emailFormFields.forEach((field) => {
      const {
        id: fieldId, title, isCustomer, isProject, isMultipleChoice,
      } = field;
      if (!fieldId) return;
      if (!isCustomer && !isProject) {
        const opt = getOption({
          fieldId,
          title,
          isMultipleChoice,
          type,
          t,
        });
        options.push(opt);

        if (!isMultipleChoice) {
          const bodyOpt = getOption({
            fieldId,
            title,
            isBody: true,
            type,
            t,
          });
          optionsForBody.push(bodyOpt);

          const subjectOpt = getOption({
            fieldId,
            title,
            isSubject: true,
            type,
            t,
          });
          optionsForSubject.push(subjectOpt);
        }
        return;
      }
      if (isProject) {
        const opt = getOption({
          fieldId: `${fieldId}-project`,
          title: `${t('Project')} - Billing Email`,
          isProject: true,
          isOffice: false,
          type,
          t,
        });
        options.push(opt);
        return;
      }
      const opt1 = getOption({
        fieldId: `${fieldId}-office`,
        title: `${t('Customer')} - Office Email`,
        isCustomer,
        isOffice: true,
        type,
        t,
      });
      const opt2 = getOption({
        fieldId: `${fieldId}-billing`,
        title: `${t('Customer')} - Billing Email`,
        isCustomer,
        type,
        t,
      });
      options.push(opt1);
      options.push(opt2);
    });
    return [options, optionsForSubject, optionsForBody];
  }, [emailFormFields, type]);

  // filter out recipient fields that don't have a name
  const finalEmailFormFields = useMemo(() => {
    return emailFormFields.filter(({ name }) => name);
  }, [emailFormFields]);

  return {
    additionalOptions,
    subjectOptions,
    bodyOptions,
    attachmentFieldOptions,
    dateTimeFields,
    emailFormFields: finalEmailFormFields,
  };
}
